import { useEffect } from 'react';
import { SITE_URL } from '@/constants/site';

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  canonicalPath: string;
  ogType?: 'website' | 'article';
  jsonLd?: Record<string, unknown>;
  imageUrl?: string;
  publishedTime?: string;
  modifiedTime?: string;
  authorName?: string;
}

function upsertMeta(attr: string, value: string, content: string): void {
  const isProperty = attr === 'property';
  const selector = isProperty ? `meta[property="${value}"]` : `meta[name="${value}"]`;
  let el: HTMLMetaElement | null = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(isProperty ? 'property' : 'name', value);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertLink(rel: string, href: string): void {
  let el: HTMLLinkElement | null = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function upsertJsonLd(jsonLd: Record<string, unknown>): void {
  const existingScript = document.head.querySelectorAll('script[type="application/ld+json"]');
  existingScript.forEach((s) => {
    if (s.getAttribute('data-dynamic-seo') === 'true') {
      s.remove();
    }
  });
  const script = document.createElement('script');
  script.setAttribute('type', 'application/ld+json');
  script.setAttribute('data-dynamic-seo', 'true');
  script.textContent = JSON.stringify(jsonLd);
  document.head.appendChild(script);
}

export default function SEO({
  title,
  description,
  keywords,
  canonicalPath,
  ogType = 'website',
  jsonLd,
  imageUrl,
  publishedTime,
  modifiedTime,
}: SEOProps) {
  useEffect(() => {
    const fullUrl = `${SITE_URL}${canonicalPath}`;

    document.title = title;
    upsertMeta('name', 'description', description);
    if (keywords) upsertMeta('name', 'keywords', keywords);

    upsertMeta('property', 'og:title', title);
    upsertMeta('property', 'og:description', description);
    upsertMeta('property', 'og:url', fullUrl);
    upsertMeta('property', 'og:type', ogType);
    if (imageUrl) upsertMeta('property', 'og:image', imageUrl);

    upsertMeta('name', 'twitter:card', 'summary_large_image');
    upsertMeta('name', 'twitter:title', title);
    upsertMeta('name', 'twitter:description', description);

    upsertLink('canonical', fullUrl);

    if (publishedTime) upsertMeta('property', 'article:published_time', publishedTime);
    if (modifiedTime) upsertMeta('property', 'article:modified_time', modifiedTime);

    if (jsonLd) {
      upsertJsonLd(jsonLd);
    }

    return () => {
      const scripts = document.head.querySelectorAll('script[data-dynamic-seo="true"]');
      scripts.forEach((s) => s.remove());
    };
  }, [title, description, keywords, canonicalPath, ogType, jsonLd, imageUrl, publishedTime, modifiedTime]);

  return null;
}